/**
 * Hook that encapsulates all Build tab (Brain domain) session state.
 *
 * Replaces the 13 `useState` calls + imperative event handler in BuildTab
 * with a single `useReducer` backed by `brainSessionReducer`.
 *
 * Client messages are immutable from server events. Server events produce
 * idempotent projections of stages, approval state, and processing flags.
 */
import { useReducer, useEffect, useRef, useCallback } from 'react';
import { brainSessionReducer, INITIAL_STATE } from '../utils/brainSessionReducer.js';
import {
  shouldIgnoreStaleApproval,
  isNetworkDeployed,
} from '../utils/workbenchApprovalGate.js';

export function useBrainSession({ network, selectedNetworkId, storeConnectionId, onComplete }) {
  const [state, dispatch] = useReducer(brainSessionReducer, INITIAL_STATE);
  const isCreatingNetworkRef = useRef(false);
  const prevSelectedNetworkIdRef = useRef(selectedNetworkId);
  const prevIsProcessingRef = useRef(state.isProcessing);
  const lastFiredSeqRef = useRef(0);

  // ─── Network switch ───────────────────────────────────────────────────
  useEffect(() => {
    const prev = prevSelectedNetworkIdRef.current;
    if (prev === selectedNetworkId) return;

    const bumpRef = () => { prevSelectedNetworkIdRef.current = selectedNetworkId; };

    if (isCreatingNetworkRef.current) {
      isCreatingNetworkRef.current = false;
      bumpRef();
      return;
    }

    if (
      prev === 'new' &&
      selectedNetworkId &&
      selectedNetworkId !== 'new' &&
      network?.networkId === selectedNetworkId
    ) {
      bumpRef();
      return;
    }

    bumpRef();
    dispatch({ type: 'client/NETWORK_SWITCH' });
  }, [selectedNetworkId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Connection ID sync ───────────────────────────────────────────────
  useEffect(() => {
    if (storeConnectionId && !state.currentConnectionId) {
      dispatch({ type: 'client/SET_CONNECTION_ID', payload: { connectionId: storeConnectionId } });
    }
  }, [storeConnectionId, state.currentConnectionId]);

  // ─── Chat history hydration ───────────────────────────────────────────
  useEffect(() => {
    if (!selectedNetworkId || selectedNetworkId === 'new') return;
    if (!network || network.networkId !== selectedNetworkId) return;
    const hist = network.chatHistory;
    if (!Array.isArray(hist) || hist.length === 0) return;
    dispatch({ type: 'client/HYDRATE_CHAT_HISTORY', payload: { history: hist } });
  }, [selectedNetworkId, network?.networkId, network?.chatHistory]);

  // ─── Graph hydration ──────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedNetworkId || selectedNetworkId === 'new') return;
    if (!network || network.networkId !== selectedNetworkId) return;
    const g = network.latestGraph;
    if (!g || typeof g !== 'object') return;
    if (!g.nodes && !g.edges) return;
    dispatch({ type: 'client/HYDRATE_GRAPH', payload: { graph: g } });
  }, [selectedNetworkId, network?.networkId, network?.latestGraph]);

  // ─── Build-box hydration ──────────────────────────────────────────────
  // Rebuild the build-activity box (generated prompts + skills + consumption)
  // on revisit so it shows "what got built", like the room's worker feed.
  // Prefer the IMMUTABLE per-revision manifest (``network.latestBuild`` — the
  // GENERATED artifacts, frozen against edits); fall back to the live AGENT
  // rows (``network.agents``) for legacy networks built before manifests.
  useEffect(() => {
    if (!selectedNetworkId || selectedNetworkId === 'new') return;
    if (!network || network.networkId !== selectedNetworkId) return;
    const build = network.latestBuild;
    const agents = network.agents;
    if (!build && (!agents || typeof agents !== 'object')) return;
    dispatch({ type: 'client/HYDRATE_BUILD', payload: { build, agents } });
  }, [selectedNetworkId, network?.networkId, network?.latestBuild, network?.agents]);

  // ─── Deployed reconciler ──────────────────────────────────────────────
  useEffect(() => {
    const s = String(network?.status || '').toUpperCase();
    if (s !== 'ACTIVE' && s !== 'RELEASED') return;
    dispatch({ type: 'client/RECONCILE_DEPLOYED' });
  }, [network?.status, network?.lastSuccessfulTaskId]);

  // ─── Completion signal → onComplete callback ──────────────────────────
  useEffect(() => {
    if (prevIsProcessingRef.current && !state.isProcessing) {
      if (state._completionSignal && state._seqCounter > lastFiredSeqRef.current) {
        lastFiredSeqRef.current = state._seqCounter;
        if (onComplete) {
          onComplete();
        }
      }
    }
    prevIsProcessingRef.current = state.isProcessing;
  }, [state.isProcessing, state._completionSignal, state._seqCounter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── WebSocket event listener ─────────────────────────────────────────
  const handleWebSocketEvent = useCallback((event) => {
    if (event.type === 'connection_established') return;
    if (shouldIgnoreStaleApproval(network?.status, event, state.isProcessing)) return;

    const { networkId: eventNetworkId } = event;
    if (
      eventNetworkId &&
      selectedNetworkId &&
      selectedNetworkId !== 'new' &&
      eventNetworkId !== selectedNetworkId
    ) {
      return;
    }

    dispatch({ type: 'server/EVENT_RECEIVED', payload: { event } });
  }, [network?.status, selectedNetworkId, state.isProcessing]);

  const wsHandlerRef = useRef(handleWebSocketEvent);
  wsHandlerRef.current = handleWebSocketEvent;

  useEffect(() => {
    const handler = (e) => wsHandlerRef.current?.(e.detail);
    window.addEventListener('websocket-message', handler);
    return () => window.removeEventListener('websocket-message', handler);
  }, []);

  // ─── Convenience dispatchers ──────────────────────────────────────────

  const sendMessage = useCallback((content, { replace = false } = {}) => {
    dispatch({
      type: 'client/SEND_MESSAGE',
      payload: { id: crypto.randomUUID(), content, replace, timestamp: new Date().toISOString() },
    });
  }, []);

  const restoreApproval = useCallback((data) => {
    dispatch({ type: 'client/RESTORE_APPROVAL', payload: data });
  }, []);

  const clearApproval = useCallback((opts) => {
    dispatch({ type: 'client/CLEAR_APPROVAL', payload: opts });
  }, []);

  const recordApprovalDecision = useCallback((taskToken, decision) => {
    dispatch({ type: 'client/RECORD_APPROVAL_DECISION', payload: { taskToken, decision } });
  }, []);

  const setApprovalLoadError = useCallback((error) => {
    dispatch({ type: 'client/SET_APPROVAL_LOAD_ERROR', payload: { error } });
  }, []);

  const setPendingTaskForRetry = useCallback((taskId) => {
    dispatch({ type: 'client/SET_PENDING_TASK_FOR_RETRY', payload: { taskId } });
  }, []);

  // ─── Public API ───────────────────────────────────────────────────────

  return {
    messages: state.clientMessages,
    stages: state.stages,
    activeStage: state.activeStage,
    artifact: state.artifact,
    isProcessing: state.isProcessing,
    buildActivity: state.buildActivity,
    inputState: state.inputState,
    approvalState: state.approvalState,
    approvalDecisions: state.approvalDecisions,
    approvalLoadError: state.approvalLoadError,
    pendingTaskIdForRetry: state.pendingTaskIdForRetry,
    currentConnectionId: state.currentConnectionId,
    showApprovalBar: state.inputState === 'approval_required',
    inputDisabled: state.inputState === 'workflow_running' || state.inputState === 'approval_required',

    dispatch,
    isCreatingNetworkRef,
    sendMessage,
    restoreApproval,
    clearApproval,
    recordApprovalDecision,
    setApprovalLoadError,
    setPendingTaskForRetry,
  };
}
